import { useNavigate } from 'react-router-dom';
import TaskCard from './TaskCard';
import { KanbanCard } from '@/types/kanban';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface TaskCardWrapperProps {
  card: KanbanCard;
  columnId: string;
  onDelete?: (cardId: string) => void;
  onDragStart?: (card: KanbanCard) => void;
  onTransfer?: (cardId: string, newColumnId: string) => void;
  activeDragType?: 'column' | 'card' | null;
  activeDragItem?: any;
  isDraggingOverlay?: boolean;
}

const TaskCardWrapper = ({ card, columnId, onDelete, onDragStart, onTransfer, activeDragType = null, activeDragItem = null, isDraggingOverlay = false }: TaskCardWrapperProps) => {
  const navigate = useNavigate();
  
  // Setup sortable functionality using dnd-kit
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: card.id,
    data: { ...card, type: 'card' }
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isDraggingOverlay ? 0.5 : 1,
    zIndex: isDragging || isDraggingOverlay ? 50 : 'auto',
    boxShadow: isDragging || isDraggingOverlay ? '0 4px 16px rgba(0,0,0,0.18)' : undefined,
    scale: isDragging || isDraggingOverlay ? 0.97 : 1,
    background: isDraggingOverlay ? '#fff' : undefined,
    border: isDraggingOverlay ? '2px solid #3b82f6' : undefined,
  };
  
  const handleCardClick = () => {
    // If the card has an MFO ID, navigate to its detail page
    if (card.mfo_id) {
      navigate(`/dashboard/mfo/${card.mfo_id}`);
    }
  };

  const handleDragStart = (e: React.MouseEvent) => {
    // Prevent navigation when dragging
    e.stopPropagation();
    if (onDragStart) {
      onDragStart(card);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    // Prevent navigation when clicking delete
    e.stopPropagation();
    if (onDelete) {
      onDelete(card.id);
    }
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`cursor-pointer ${isDragging || isDraggingOverlay ? 'scale-95 shadow-lg' : ''}`}
      onClick={handleCardClick}
      {...attributes}
      {...listeners}
    >
      {/* Drop indicator */}
      {isOver && !isDragging && !isDraggingOverlay && (
        <div className="h-2 bg-primary/30 rounded mb-1 animate-pulse" />
      )}
      <TaskCard 
        card={card}
        columnId={columnId}
        onDelete={(cardId) => {
          const syntheticEvent = {
            stopPropagation: () => {}
          } as React.MouseEvent;
          handleDeleteClick(syntheticEvent);
        }}
        onDragStart={handleDragStart}
        isDisabled={isDragging || isDraggingOverlay}
        onTransfer={onTransfer}
      />
    </div>
  );
};

export default TaskCardWrapper;
